import { delay } from 'common/utils'; // Replace with custom delay if needed in Rematch
import { Logger } from 'common/logger';
import { GET_CURRENT, GET_HISTORY } from 'common/webSocketMiddleware.constants';
import { getWebsocketUrlForPark } from 'common/webSocket.utils';

export const webSocket = {
    name: 'webSocket',
    state: {
        message: '',
        autoDismiss: false,
        colour: 'yellow',
        disconnectionTimestamp: null,
        connected: false,
        connecting: false,
        reconnect: true,
    },

    reducers: {
        updateStatus(state, status) {
            const firstTimestamp =
                state.disconnectionTimestamp && status.disconnectionTimestamp
                    ? state.disconnectionTimestamp
                    : status.disconnectionTimestamp;

            return { ...state, ...status, disconnectionTimestamp: firstTimestamp };
        },
    },

    effects: (dispatch) => ({
        async connect(_, rootState) {
            Logger.info('Connect websocket');
            const selectedPark = rootState.appData.selectedPark.toLowerCase();
            const { token } = rootState.account;

            if (token) {
                const URL = getWebsocketUrlForPark(selectedPark, token);
                dispatch({ type: 'WEBSOCKET/CONNECT', URL });
            } else {
                Logger.warn('Cannot connect WS. No token');
            }
        },

        async disconnect() {
            Logger.info('Disconnect websocket');
            dispatch({ type: 'updateStatus', payload: { reconnect: false } });
            dispatch({ type: 'WEBSOCKET/DISCONNECT' });
        },

        async isConnecting() {
            dispatch({
                type: 'webSocket/updateStatus',
                payload: {
                    message: 'Connecting to server...',
                    autoDismiss: false,
                    connecting: true,
                    connected: false,
                    disconnectionTimestamp: new Date(),
                    colour: 'yellow',
                    reconnect: true,
                },
            });
        },

        async isConnected() {
            dispatch({
                type: 'webSocket/updateStatus',
                payload: {
                    message: 'Connected to server',
                    autoDismiss: true,
                    connecting: false,
                    connected: true,
                    disconnectionTimestamp: null,
                    colour: 'green',
                    reconnect: true,
                },
            });
        },

        async isDisconnected(_, rootState) {
            dispatch({
                type: 'webSocket/updateStatus',
                payload: {
                    message: 'Server connection is closed',
                    autoDismiss: false,
                    connecting: false,
                    connected: false,
                    disconnectionTimestamp: new Date(),
                    colour: 'red',
                    reconnect: true,
                },
            });
            const shouldReconnect = rootState.webSocket.reconnect;
            if (shouldReconnect) {
                await delay(5000);
                await dispatch.webSocket.connect();
            }
        },

        async handleError() {
            dispatch({
                type: 'webSocket/updateStatus',
                payload: {
                    message: 'Server connection error',
                    autoDismiss: false,
                    connected: false,
                    connecting: false,
                    disconnectionTimestamp: new Date(),
                    colour: 'red',
                    reconnect: true,
                },
            });
        },

        async requestCurrentState() {
            dispatch({ type: GET_CURRENT });
        },

        async requestTurbineHistory({ entityKey, precision, count }) {
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType: 'TURBINE',
                    entityKey,
                    precision,
                    count,
                    fields: [
                        'active_power',
                        'reactive_power',
                        'wind_speed',
                        'nacelle_direction',
                        'ambient_temperature',
                    ],
                },
            });
        },

        async requestWeatherHistory(payload) {
            const { entityKey, precision, count } = payload;
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType: 'WEATHER',
                    entityKey,
                    precision,
                    count,
                    fields: [
                        'wind_speed',
                        'wave_height',
                        'ambient_temperature',
                        'tide_height',
                        'wind_direction',
                        'water_temperature',
                    ],
                },
            });
        },
        requestPersonHistory(payload) {
            const { entityKey, entityType, count, fields } = payload;
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType,
                    entityKey,
                    count,
                    fields,
                },
            });
        },
        async requestPlantHistory(payload) {
            const { entityKey, precision, count } = payload;
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType: 'POWER_PLANT',
                    entityKey,
                    precision,
                    count,
                    fields: ['active_power', 'reactive_power', 'park_pilot'],
                },
            });
        },

        async requestGridConnectionHistory(payload) {
            const { entityKey, precision, count } = payload;
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType: 'GRID_CONNECTION',
                    entityKey,
                    precision,
                    count,
                    fields: ['active_power', 'reactive_power'],
                },
            });
        },

        async requestHistoricalProductionForecast(payload) {
            const { entityKey, precision, count } = payload;
            dispatch({
                type: GET_HISTORY,
                payload: {
                    entityType: 'HISTORICAL_PRODUCTION_FORECAST',
                    entityKey,
                    precision,
                    count,
                    fields: ['active_power'],
                },
            });
        },
    }),
};
